import React, { useEffect } from "react";
import { Modal, Form, Input, Select, Spin } from "antd";
import { RootState, useAppDispatch, useAppSelector } from "../store";
import { GetCountryListAPI } from "../Admin/Redux/country";
import { svg } from "../Assets/svg";

interface SelectOffSiteLocationModalProps { 
  open: boolean;
  onCancel: () => void;
  onSave: (values: { off_site_loc_text?: string; country?: string }) => void;
  loading?: boolean;
}

const SelectOffSiteLocationModal: React.FC<SelectOffSiteLocationModalProps> = ({
  open,
  onCancel,
  onSave,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { countryList = [] } = useAppSelector((state: RootState) => state?.country || {});

  useEffect(() => {
    if (open) {
      if (!countryList || countryList.length === 0) {
        dispatch(GetCountryListAPI() as any);
      }
    } else {
      form.resetFields();
    }
  }, [open, dispatch, countryList]);

  const handleFinish = (values: any) => {
    onSave({
      off_site_loc_text: values.off_site_loc_text,
      country: values.country,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={loading ? undefined : onCancel}
      footer={null}
      width={520}
      closeIcon={loading ? null : svg?.closeIcon}
      closable={!loading}
      maskClosable={!loading}
      centered
      destroyOnClose
      zIndex={10050}
      getContainer={() => document.body}
      className="custom-modal"
    >
      <div className="modal-content border-0 p-0 pt-0 page-form">
        <div className="modal-header border-0 pb-1 mb-2">
          <h1 className="modal-title fs-4 fw-bolder mx-auto text-dark" id="selectOffSiteLocationTitle">
            Select Off Site Location
          </h1>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="modal-body pb-0"
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault();
          }}
        >
          <div className="form-item mb-3">
            <label className="custom-label mb-1 ff">Off Site Location</label>
            <Form.Item
              name="off_site_loc_text"
              className="mb-0"
              rules={[{ required: true, message: "Please enter off site location" }]}
            >
              <Input
                placeholder="Enter off site location"
                className="w-100"
                size="large"
                disabled={loading}
              />
            </Form.Item>
          </div>

          <div className="form-item mb-3">
            <label className="custom-label mb-1 ff">Country</label>
            <Form.Item
              name="country"
              className="mb-0"
              rules={[{ required: true, message: "Please select country" }]}
            >
              <Select
                showSearch
                placeholder="Select Country"
                className="w-100"
                size="large"
                disabled={loading}
                filterOption={(input, option) =>
                  String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={countryList.map((c: any) => ({
                  label: c.country_name,
                  value: c._id,
                }))}
                suffixIcon={svg?.selectDownArrow}
              />
            </Form.Item>
          </div>

          <div className="d-flex justify-content-center gap-3 mt-4 mb-3">
            <button
              type="button"
              className="btn btn-outline-dark fs-6 fw-bold px-4 py-2"
              onClick={onCancel}
              disabled={loading}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="btn btn-dark fs-6 fw-bold px-4 py-2 d-flex align-items-center justify-content-center gap-2"
              style={{ minWidth: "110px" }}
              disabled={loading}
            >
              {loading && <Spin size="small" style={{ filter: "brightness(0) invert(1)" }} />}
              <span>SAVE</span>
            </button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default SelectOffSiteLocationModal;
